import './EquipmentLibraryDrawer.css';
import type {
  WeaponManifestEntry,
  WeaponCategory,
} from '../../services/host/WeaponsServiceModule';
import type { EquipmentLibraryState } from '../../hooks/useEquipmentLibrary';

/**
 * EquipmentLibraryDrawer — 2026-06-08 Phase 1E wire-up for the
 * canonical Q Manual VG35002 weapons catalog.
 *
 * Lists every weapon in the manifest grouped by category (5 sections:
 * Pistols / Rifles, SMG, Shotguns / Misc Modern / Accessories /
 * Archaic). Each row is a clickable button that fires
 * onSelect(weapon_id) — the ScenarioRunner host wires the click into a
 * detail expansion showing the loaded weapon's stat block + description
 * + Q evaluation + game information.
 *
 * 3-state union per useEquipmentLibrary (mirror of useModulesLibrary):
 * renders a loading affordance when state==='loading', error message
 * when state==='error', and the grouped list when state==='ready'.
 *
 * Mirrors ModulesLibraryDrawer.tsx pattern. Scope: weapons only this
 * phase (vehicles + gadgets + drugs + Q Branch NPCs come in subsequent
 * phases). The drawer can be extended with category-tabs when additional
 * categories ship.
 */

export interface EquipmentLibraryDrawerProps {
  weapons: WeaponManifestEntry[];
  state: EquipmentLibraryState;
  error?: string | null;
  selectedWeaponId: string | null;
  selectedWeaponData: Record<string, unknown> | null;
  selectedWeaponState: EquipmentLibraryState;
  onSelect: (weaponId: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

interface CategoryDef {
  key: WeaponCategory;
  label: string;
}

const CATEGORY_ORDER: ReadonlyArray<CategoryDef> = [
  { key: 'pistol', label: 'Pistols' },
  { key: 'rifle_smg_shotgun', label: 'Rifles, SMGs, Shotguns' },
  { key: 'miscellaneous_modern_weapon', label: 'Miscellaneous Modern' },
  { key: 'weapons_accessory', label: 'Accessories' },
  { key: 'archaic_weapon', label: 'Archaic Weapons' },
];

function groupByCategory(
  weapons: WeaponManifestEntry[],
): Map<WeaponCategory, WeaponManifestEntry[]> {
  const groups = new Map<WeaponCategory, WeaponManifestEntry[]>();
  for (const w of weapons) {
    const list = groups.get(w.category) ?? [];
    list.push(w);
    groups.set(w.category, list);
  }
  return groups;
}

/**
 * Render a stat block + narrative summary for the selected weapon.
 *
 * The stat block is a flat key/value grid that adapts to the seed's
 * actual stat fields (different weapons carry different combinations —
 * archaic melee weapons have no AMMO/JAM/RL, missile launchers have
 * RANGE/SPEED instead of CLOSE/LONG, etc.).
 */
function WeaponDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const stats = (data['stats'] ?? {}) as Record<string, unknown>;
  const description = (data['description'] as string) ?? '';
  const qEvaluation = (data['q_evaluation'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;

  // Render stat fields in a sensible order. Order keys take a stable
  // display order; any non-stat seed fields surface as ".other" rows
  // so future schema additions are still visible without code changes.
  const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'pm', label: 'PM' },
    { key: 's_r', label: 'S/R' },
    { key: 'ammo', label: 'AMMO' },
    { key: 'dc', label: 'DC' },
    { key: 'close_inches', label: 'CLOSE' },
    { key: 'long_inches', label: 'LONG' },
    { key: 'con', label: 'CON' },
    { key: 'jam', label: 'JAM' },
    { key: 'draw', label: 'DRAW' },
    { key: 'rl', label: 'RL' },
    { key: 'cost_gbp', label: 'COST (£)' },
  ];
  const known = new Set(STAT_ORDER.map((s) => s.key));
  const extras = Object.keys(stats).filter((k) => !known.has(k));

  function formatStat(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(' / ');
    return String(value);
  }

  return (
    <div
      className="br-equipment-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <h3 className="br-equipment-detail-title">
        {(data['name'] as string) ?? '—'}
      </h3>
      {page !== undefined && (
        <p
          className="br-equipment-detail-page br-label"
          data-testid={`${testidPrefix}-detail-page`}
        >
          Q Manual p.{page}
        </p>
      )}
      <dl className="br-equipment-detail-stats">
        {STAT_ORDER.map((s) =>
          s.key in stats ? (
            <div key={s.key}>
              <dt>{s.label}</dt>
              <dd className="br-mono">{formatStat(stats[s.key])}</dd>
            </div>
          ) : null,
        )}
        {extras.map((k) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd className="br-mono">{formatStat(stats[k])}</dd>
          </div>
        ))}
      </dl>
      {description !== '' && (
        <p
          className="br-equipment-detail-text"
          data-testid={`${testidPrefix}-detail-description`}
        >
          {description}
        </p>
      )}
      {qEvaluation !== '' && (
        <p
          className="br-equipment-detail-text br-equipment-detail-q"
          data-testid={`${testidPrefix}-detail-q-evaluation`}
        >
          <em>Q evaluation:</em> {qEvaluation}
        </p>
      )}
      {gameInformation !== '' && (
        <p
          className="br-equipment-detail-text br-equipment-detail-game"
          data-testid={`${testidPrefix}-detail-game-info`}
        >
          <strong>Game info:</strong> {gameInformation}
        </p>
      )}
    </div>
  );
}

export function EquipmentLibraryDrawer({
  weapons,
  state,
  error,
  selectedWeaponId,
  selectedWeaponData,
  selectedWeaponState,
  onSelect,
  onClose,
  testidPrefix = 'br-equipment-library',
}: EquipmentLibraryDrawerProps) {
  const groups = groupByCategory(weapons);

  return (
    <aside
      className="br-scenario-drawer br-equipment-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header className="br-equipment-library-header">
        <span className="br-equipment-library-title">Equipment Library</span>
        <span
          className="br-equipment-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {weapons.length}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-equipment-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close equipment library"
          >
            ✕
          </button>
        )}
      </header>

      <p
        className="br-equipment-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Q Manual VG35002 weapons catalog — Pistols, Rifles/SMG/Shotguns,
        Miscellaneous Modern Weapons, Accessories, and Archaic Weapons. Click
        a row to expand its stat block + Q evaluation + game info. Vehicles,
        gadgets, drugs, and Q Branch personnel will be added in subsequent
        phases.
      </p>

      {state === 'loading' && (
        <p
          className="br-equipment-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading weapons…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-equipment-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load weapons: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && weapons.length === 0 && (
        <p
          className="br-equipment-library-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No weapons in seed/canonical/equipment/weapons.json.
        </p>
      )}

      {state === 'ready' && weapons.length > 0 && (
        <div
          className="br-equipment-library-list"
          data-testid={`${testidPrefix}-list`}
        >
          {CATEGORY_ORDER.map((catDef) => {
            const items = groups.get(catDef.key) ?? [];
            if (items.length === 0) return null;
            return (
              <section
                key={catDef.key}
                className="br-equipment-library-group"
                data-category={catDef.key}
                data-testid={`${testidPrefix}-group-${catDef.key}`}
              >
                <h4 className="br-equipment-library-group-title">
                  {catDef.label} ({items.length})
                </h4>
                <ul className="br-equipment-library-rows">
                  {items.map((w) => {
                    const isSelected = w.id === selectedWeaponId;
                    return (
                      <li
                        key={w.id}
                        className="br-equipment-library-row-wrapper"
                      >
                        <button
                          type="button"
                          className={
                            isSelected
                              ? 'br-equipment-library-row br-equipment-library-row-selected'
                              : 'br-equipment-library-row'
                          }
                          data-testid={`${testidPrefix}-row-${w.id}`}
                          data-selected={isSelected ? 'true' : 'false'}
                          onClick={() => onSelect(w.id)}
                          aria-pressed={isSelected}
                        >
                          <span className="br-equipment-library-row-title">
                            {w.name}
                          </span>
                          {w.page !== null && (
                            <span className="br-equipment-library-row-meta br-label">
                              p.{w.page}
                            </span>
                          )}
                        </button>
                        {isSelected && selectedWeaponState === 'loading' && (
                          <p
                            className="br-equipment-library-detail-loading"
                            data-testid={`${testidPrefix}-detail-loading`}
                          >
                            Loading…
                          </p>
                        )}
                        {isSelected && selectedWeaponState === 'error' && (
                          <p
                            className="br-equipment-library-detail-error"
                            data-testid={`${testidPrefix}-detail-error`}
                          >
                            Could not load weapon.
                          </p>
                        )}
                        {isSelected &&
                          selectedWeaponState === 'ready' &&
                          selectedWeaponData !== null && (
                            <WeaponDetail
                              data={selectedWeaponData}
                              testidPrefix={testidPrefix}
                            />
                          )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}
